import { Grid, Theme } from "@mui/material";
import { useMemo } from "react";
import { IFlightRelease, IUser, FlightRemovalType } from "@vmosolutions/oc-interfaces";
import { formatDateString } from "../DateDisplay";
import { ShowUserInfo } from "../ShowUserInfo/ShowUserInfo";

export interface ILastModifiedInfo {
  modifiedBy: string;
  modifiedAt: Date;
}

interface IDisplayListModificationProps {
  userId: string;
  time: Date | number;
  released?: IFlightRelease;
  outsideModification?: ILastModifiedInfo;
  removed?: {
    removalType: FlightRemovalType;
    removedBy: string;
    removedAt: Date | number;
  };
}

/**
 * A component to display the modified at fields and the modified by fields
 * @param props- userId - the id of the user who made the changes & the time
 */
export const DisplayLastModification = (props: IDisplayListModificationProps): JSX.Element => {
  const { userId, time, released, outsideModification, removed } = props;

  const releaseTime = released ? `${formatDateString(released.releasedAt, "Etc/UTC", true)} ` : "";

  // Return true if there's outside modification info and it's date exceeds the time passed in.
  const flightModifiedOutsideEditor = useMemo(() => {
    return outsideModification?.modifiedAt && time ? new Date(outsideModification?.modifiedAt) > new Date(time) : false;
  }, [outsideModification?.modifiedAt, time]);

  const displayUser = flightModifiedOutsideEditor
    ? outsideModification.modifiedBy
    : (userId ?? outsideModification.modifiedBy);
  const displayTime = flightModifiedOutsideEditor
    ? outsideModification.modifiedAt
    : (time ?? outsideModification.modifiedAt);
  const atTimeString = ` on ${formatDateString(displayTime, "Etc/UTC", true)} `;

  const removedAtTimeString = removed ? ` on ${formatDateString(removed.removedAt, "Etc/UTC", true)} ` : "";
  const DisplayLastModificationStyles = (theme: Theme): any => ({
    "@keyframes flash": {
      "0%": { color: theme.palette.secondary.main },
      "25%": { color: theme.palette.primary.contrastText },
      "50%": { color: theme.palette.secondary.main },
      "75%": { color: theme.palette.primary.contrastText },
      "100%": { color: theme.palette.secondary.main },
    },
    root: {
      paddingLeft: "10px",
      "& .modified_span": {
        color: theme.custom.forms.labels,
        margin: 0,
        fontSize: 10,
        height: "2.2em",
        alignSelf: "center",
      },
      "&.warning_flash .last_modified_info": {
        color: theme.palette.secondary.main,
        animation: "3s linear normal forwards ease-in-out",
        animationName: "$flash",
      },
    },
  });

  return (
    <Grid
      container
      className={`${flightModifiedOutsideEditor ? "warning_flash" : ""}`}
      sx={(theme) => DisplayLastModificationStyles(theme).root}
    >
      <span className="modified_span" style={{ marginLeft: 3 }}>
        <div className="last_modified_info">
          Last Modified By:
          <ShowUserInfo userId={displayUser} getUserLabel={(user: IUser) => ` ${user.firstName} ${user.lastName}`} />
          {atTimeString}
        </div>
        {released && (
          <div>
            Released by{" "}
            <ShowUserInfo
              userId={released.releasedBy}
              getUserLabel={(user: IUser) => ` ${user.firstName} ${user.lastName}`}
            />{" "}
            on {releaseTime}
          </div>
        )}
        {removed && (
          <div>
            {removed.removalType === FlightRemovalType.CancelFlight ? "Canceled by " : "Deleted by "}
            <ShowUserInfo
              userId={removed.removedBy}
              getUserLabel={(user: IUser) => ` ${user.firstName} ${user.lastName}`}
            />{" "}
            {removedAtTimeString}
          </div>
        )}
      </span>
    </Grid>
  );
};
